import { useEffect, useState } from "react";
import { Button, Modal, Placeholder, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import { MDFeNaoEncerradoItemOutput } from "../MDFe.Dto";
import { MDFeNaoEncerrados } from "src/services/Fiscal.Service";
import { showToast } from "src/componentes";
import { MapearErrosServidor } from "src/hook/Functions";

interface Props {
  show: boolean;
  onClose: () => void;
  onEncerrar?: (chaveAcesso: string, protocolo: string) => void;
}

const MDFeModalNaoEncerradosView = ({ show, onClose, onEncerrar }: Props) => {
  const [data, setData] = useState<MDFeNaoEncerradoItemOutput[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const carregarNaoEncerrados = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await MDFeNaoEncerrados();
      if (response.data.success) {
        setData(response.data.data || []);
      } else {
        setData([]);
        const notificacoes = response.data.notifications;
        const msg =
          Array.isArray(notificacoes) && notificacoes.length > 0
            ? notificacoes
                .map((n: any) =>
                  typeof n === "string" ? n : n.message || n.mensagem || JSON.stringify(n)
                )
                .filter(Boolean)
                .join(" | ")
            : "Falha ao consultar manifestos não encerrados na SEFAZ.";
        setErrorMessage(msg);
        MapearErrosServidor(response.data.notifications, showToast);
      }
    } catch (error: any) {
      console.error("Erro ao buscar manifestos não encerrados:", error);
      setData([]);
      const notificacoes = error?.response?.data?.notifications;
      const msg =
        (Array.isArray(notificacoes) &&
          notificacoes.length > 0 &&
          notificacoes
            .map((n: any) =>
              typeof n === "string" ? n : n.message || n.mensagem || JSON.stringify(n)
            )
            .filter(Boolean)
            .join(" | ")) ||
        error?.response?.data?.message ||
        "Não foi possível consultar os manifestos não encerrados na SEFAZ. Verifique as configurações e tente novamente.";
      setErrorMessage(msg);
      showToast({
        message: "Ocorreu erro ao consultar manifestos não encerrados",
        title: "Erro",
        variant: "Danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      setErrorMessage(null);
      carregarNaoEncerrados();
    }
  }, [show]);

  const handleCopy = (chave: string) => {
    navigator.clipboard.writeText(chave);
    setCopiedKey(chave);
    showToast({
      message: "Chave de acesso copiada para a área de transferência",
      title: "Copiado",
      variant: "Success",
    });
    setTimeout(() => setCopiedKey(null), 2500);
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="text-warning d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faExclamationTriangle} /> Manifestos Não Encerrados na SEFAZ
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
          Lista de MDF-es que constam como autorizados e ainda não encerrados na SEFAZ para esta empresa. Placas vinculadas a esses manifestos podem ser bloqueadas para emissão de novos documentos (Rejeição 611).
        </p>

        {loading ? (
          <Placeholder animation="glow">
            <Placeholder xs={12} size="lg" className="mb-2" />
            <Placeholder xs={12} size="lg" className="mb-2" />
          </Placeholder>
        ) : errorMessage ? (
          <div className="alert alert-danger mb-0">
            <div className="d-flex align-items-center gap-2 fw-bold text-danger mb-2">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>Falha na Consulta à SEFAZ</span>
            </div>
            <p className="mb-2 small">{errorMessage}</p>
            <div className="small text-muted border-top border-danger border-opacity-25 pt-2">
              Não foi possível verificar se existem manifestos pendentes. Por favor, verifique as configurações (como Tipo de Ambiente e Certificado) ou clique em <strong>Atualizar Consulta</strong> para tentar novamente.
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="alert alert-success mb-0">
            Nenhum manifesto com pendência de encerramento encontrado na SEFAZ. Veículos e placas liberados!
          </div>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>Chave de Acesso</th>
                <th>Protocolo</th>
                <th className="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item.chaveAcesso}>
                  <td className="align-middle font-monospace small">
                    {item.chaveAcesso}
                  </td>
                  <td className="align-middle">{item.numeroProtocolo}</td>
                  <td className="text-end align-middle">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2"
                      title="Copiar Chave"
                      onClick={() => handleCopy(item.chaveAcesso)}
                    >
                      <FontAwesomeIcon
                        icon={copiedKey === item.chaveAcesso ? faCheck : faCopy}
                      />
                    </Button>
                    {onEncerrar && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => {
                          onClose();
                          onEncerrar(item.chaveAcesso, item.numeroProtocolo);
                        }}
                      >
                        Encerrar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          className="me-auto"
          onClick={carregarNaoEncerrados}
          disabled={loading}
        >
          Atualizar Consulta
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MDFeModalNaoEncerradosView;
